import { ArrowBack } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Container,
    IconButton,
    LinearProgress,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useUser } from 'src/allauth/auth';
import AthleteDetailView from 'src/components/AthleteDetailView';
import DailyMoodCheckInButton from 'src/components/DailyMoodCheckInButton';
import UserPointsTotal from 'src/components/UserPointsTotal';
import ApiClient from '../config/ApiClient';
import { AthletePaymentAssignment } from '../object-actions/types/types';

const ProgressReport: React.FC = () => {
    const { assignment_id, athlete_id } = useParams<{ assignment_id?: string; athlete_id?: string }>();
    const location = useLocation();
    const user = useUser();
    const [assignment, setAssignment] = useState<AthletePaymentAssignment | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const assignmentFromState = location.state?.assignment as AthletePaymentAssignment | undefined;

    const fetchAssignment = useCallback(async () => {
        if (!assignment_id && !athlete_id) {
            setError('Assignment ID is required');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const url = assignment_id
                ? `/api/athlete-assignments-by-id/${assignment_id}`
                : `/api/athlete-assignments/${athlete_id}`;
            const response = await ApiClient.get<AthletePaymentAssignment>(url);

            if (response.error) {
                setError(response.error);
            } else if (response.success && response.data) {
                setAssignment(response.data);
            } else {
                setError('No assignment found');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch assignment data');
        } finally {
            setLoading(false);
        }
    }, [assignment_id, athlete_id]);

    useEffect(() => {
        if (assignmentFromState) {
            const matchById = assignment_id && assignmentFromState.id === parseInt(assignment_id, 10);
            const matchByAthlete = athlete_id && assignmentFromState.athlete_id === parseInt(athlete_id, 10);
            if (matchById || matchByAthlete) {
                setAssignment(assignmentFromState);
            }
        }
        fetchAssignment();
    }, [assignment_id, athlete_id, assignmentFromState, fetchAssignment]);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <LinearProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    if (!assignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No assignment found.
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Box p={2} data-testid="progress-report">
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, alignContent: 'center', justifyItems: 'center' }}>
                <IconButton
                    component={Link}
                    to="/dashboard"
                    color="secondary"
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
                    {assignment.athlete.id == user?.id ? 'My Progress' : 'Program Progress: ' + assignment.athlete.str}
                </Typography>

                {assignment.athlete.id == user?.id && <React.Fragment>
                    <DailyMoodCheckInButton />
                    <UserPointsTotal />
                </React.Fragment>}
            </Box>

            <AthleteDetailView assignment={assignment} />
        </Box>
    );
};

export default ProgressReport;
